'use client';
import classNames from 'classnames';
import Link from 'next/link';
import type { ReactNode } from 'react';

import { useContactsData } from '@/entities/contacts';
import ContactImage from '@/entities/gallery/ui/contactImage/ContactImage';
import { formatPhoneDisplay } from '@/shared/lib';
import { QueryStatus } from '@/shared/ui';
import { MODAL_TYPES } from '@/widgets/modals/config/constants';
import { useModalStore } from '@/widgets/modals/model/store';

import styles from './contactsPage.module.scss';

const ContactsPage = ({ breadcrumbs }: { breadcrumbs?: ReactNode }) => {
  const { openModal } = useModalStore();
  const { data, isLoading, isError, refetch } = useContactsData();

  const handleOpenModal = () => {
    openModal(MODAL_TYPES.contactModal);
  };

  function renderImage(
    item: { image: string; id: number },
    index: number,
    keyPrefix: string = '',
  ) {
    return (
      <ContactImage
        key={`${keyPrefix}contact_image_${item.id}_${index}`}
        className={styles.contacts__image}
        image={item.image}
        title={keyPrefix === 'second_' ? '' : 'Фотография Black Tea Room'}
      />
    );
  }

  if (!data)
    return (
      <main className={classNames('page', styles.contacts)}>
        <div className="container">
          <h1 className={styles.contacts__title}>Контакты</h1>
          <QueryStatus
            isError={isError}
            isLoading={isLoading}
            onRetry={() => refetch()}
          />
          {breadcrumbs}
        </div>
      </main>
    );

  return (
    <main className={classNames('page', styles.contacts)}>
      <div className={classNames('container', styles.contacts__wrapper)}>
        <div className={styles.contacts__content}>
          <div className={styles.contacts__content_top}>
            <div>
              <h1 className={styles.contacts__title}>Контакты</h1>
            </div>
            <div>
              <div className={styles.contacts__row}>
                <p
                  className={classNames(
                    styles.contacts__text,
                    styles['contacts__text--bold'],
                  )}
                >
                  Наш адрес:
                </p>
              </div>
              <div
                className={classNames(
                  styles.contacts__row,
                  styles['contacts__row--flex'],
                  styles['contacts__row--address'],
                )}
              >
                <p className={styles.contacts__text}>{data?.address}</p>
                <p className={styles.contacts__text}>{data?.work_time}</p>
              </div>
              <div
                className={classNames(
                  styles.contacts__row,
                  styles['contacts__row--link'],
                )}
              >
                <button
                  className={classNames(
                    styles.contacts__text,
                    styles.contacts__link,
                  )}
                  onClick={handleOpenModal}
                >
                  Как пройти
                </button>
              </div>
              <div
                className={classNames(
                  styles.contacts__row,
                  styles['contacts__row--text'],
                )}
              >
                <p
                  className={classNames(
                    styles.contacts__text,
                    styles['contacts__text--desc'],
                  )}
                >
                  Если вы собираетесь зайти к нам в Шоу-рум и вам нужна
                  консультация, пожалуйста,
                  <br className={classNames('is-desk')} /> позвоните нам по
                  телефону{' '}
                  {data.phone && (
                    <Link href={`tel:${data.phone}`}>
                      <b>{formatPhoneDisplay(data?.phone ?? '')}</b>
                    </Link>
                  )}{' '}
                  или напишите на почту{' '}
                  <br className={styles.contacts__break} />
                  {data.email && (
                    <Link
                      className={classNames(
                        styles.contacts__link,
                        styles['contacts__link--email'],
                      )}
                      href={`mailto:${data?.email ?? ''}`}
                      rel="noopener nofollow"
                      target="_blank"
                    >
                      {data.email}
                    </Link>
                  )}
                </p>
              </div>
              <div
                className={classNames(
                  styles.contacts__row,
                  styles['contacts__row--flex'],
                  styles['contacts__row--social'],
                )}
              >
                {data.telegram_url && (
                  <Link
                    className={classNames(
                      styles.contacts__text,
                      styles['contacts__text--link'],
                    )}
                    href={data?.telegram_url ?? ''}
                    target="_blank"
                  >
                    Телеграм
                  </Link>
                )}
                {data.vkontakte_url && (
                  <Link
                    className={classNames(
                      styles.contacts__text,
                      styles['contacts__text--link'],
                    )}
                    href={data?.vkontakte_url ?? ''}
                    target="_blank"
                  >
                    Вконтакте
                  </Link>
                )}
              </div>
            </div>
          </div>
          <div className={styles.contacts__content_gallery}>
            <div className={styles.contacts__gallery_track}>
              {/* Первый набор изображений */}
              {data?.images?.map((item, index) =>
                renderImage(item, index, 'first_'),
              )}
              {/* Дублированный набор для бесконечной прокрутки */}
              {data?.images?.map((item, index) =>
                renderImage(item, index, 'second_'),
              )}
            </div>
          </div>
        </div>
        {breadcrumbs}
      </div>
    </main>
  );
};

export default ContactsPage;
